import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, UserCircle2, KeyRound, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CoachesPage() {
  const [coaches, setCoaches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  // Reset password dialog state
  const [resetResult, setResetResult] = useState(null); // {email, temp_password}

  const load = () => {
    api.get("/users/coaches").then((r) => setCoaches(r.data));
    api.get("/teams").then((r) => setTeams(r.data));
  };
  useEffect(() => { load(); }, []);

  const coachTeams = (coach) => {
    const ids = coach.team_ids || (coach.team_id ? [coach.team_id] : []);
    return ids
      .map((tid) => {
        const t = teams.find((x) => x.id === tid);
        if (!t) return null;
        let role = null;
        if (t.coach_id === coach.id) role = "Principal";
        else if (t.assistant_coach_id === coach.id) role = "Ayudante";
        else if (t.fitness_coach_id === coach.id) role = "Preparador físico";
        else if (t.delegate_id === coach.id) role = "Delegado";
        return { id: tid, name: t.name, role };
      })
      .filter(Boolean);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", { ...form, role: "coach" });
      toast.success("Entrenador creado");
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPassword = async (c) => {
    if (!window.confirm(`¿Generar nueva contraseña temporal para ${c.name}?`)) return;
    try {
      const { data } = await api.post(`/users/coaches/${c.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeCoach = async (c) => {
    if (
      !window.confirm(
        `¿Eliminar al entrenador ${c.name}?\n\nQuedará desasignado de su equipo y no podrá iniciar sesión. Esta acción es irreversible.`,
      )
    )
      return;
    try {
      await api.delete(`/users/coaches/${c.id}`);
      toast.success("Entrenador eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyTemp = () => {
    if (!resetResult) return;
    navigator.clipboard?.writeText(resetResult.temp_password);
    toast.success("Contraseña copiada al portapapeles");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>Entrenadores</h1>
          <p className="text-slate-500 text-sm mt-1">Gestiona el cuerpo técnico del club.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="new-coach-button">
              <Plus className="h-4 w-4 mr-2" /> Nuevo entrenador
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Registrar entrenador</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coach-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coach-email-input" />
              </div>
              <div>
                <Label>Contraseña temporal</Label>
                <Input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="coach-password-input" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="coach-save-button">Crear</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {coaches.length === 0 && <div className="text-slate-500 text-sm col-span-full">Aún no hay entrenadores.</div>}
        {coaches.map((c) => (
          <Card key={c.id} className="border-slate-200 shadow-sm" data-testid={`coach-card-${c.id}`}>
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-purple-100 text-purple-800 grid place-items-center">
                <UserCircle2 className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-lg" style={{ fontFamily: "Nunito" }}>{c.name}</div>
                <div className="text-sm text-slate-500 truncate">{c.email}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">
                  {(() => {
                    const list = coachTeams(c);
                    if (list.length === 0) {
                      return <>Equipos: <span className="text-slate-400 font-bold">Sin equipo</span></>;
                    }
                    return (
                      <div className="flex flex-col gap-1">
                        <span>Equipos ({list.length}):</span>
                        <div className="flex flex-wrap gap-1.5 normal-case tracking-normal">
                          {list.map((t) => (
                            <span
                              key={t.id}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-[11px] font-bold"
                              data-testid={`coach-team-${c.id}-${t.id}`}
                            >
                              {t.name}
                              {t.role && (
                                <span className="text-[9px] font-black bg-white/60 rounded px-1 py-px">
                                  {t.role}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetPassword(c)}
                    data-testid={`reset-password-${c.id}`}
                  >
                    <KeyRound className="h-3.5 w-3.5 mr-2" /> Resetear contraseña
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeCoach(c)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    data-testid={`delete-coach-${c.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Eliminar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reset-password result dialog */}
      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent data-testid="reset-result-dialog">
          <DialogHeader>
            <DialogTitle>Contraseña temporal generada</DialogTitle>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Pásale estos datos al entrenador. Cuando inicie sesión podrá cambiarla más tarde.
              </p>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Email</div>
                <div className="font-mono text-sm font-bold" data-testid="reset-email">{resetResult.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 pt-2">Contraseña temporal</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-base font-bold text-purple-800 bg-white border border-slate-200 rounded px-3 py-2" data-testid="reset-temp-password">
                    {resetResult.temp_password}
                  </code>
                  <Button size="icon" variant="outline" onClick={copyTemp} data-testid="copy-temp-password">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setResetResult(null)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="reset-close-button">
                  Entendido
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
